/**
 * مافیا روی وب — عددها و متن‌ها.
 *
 * عددها همان enum های موتور‌اند (MobileGame/Assets/Scripts/Core/MafiaTypes.cs ⇄ Backend/internal/mafia/types.go)
 * و روی سیم همین‌طور می‌آیند (MafiaState، MafiaEvent). متن‌ها همان MafiaText ِ اپ‌اند — محاوره‌ی تهرانی و
 * کوتاه — تا یک میز در اپ و وب یک جور حرف بزند.
 */
import { fa } from "../game/cards";
import { ROLES, SCENARIOS } from "./data";

export const Team = { Citizen: 0, Mafia: 1, Independent: 2 } as const;

export const Ask = {
  Speak: 1, Status: 2, Vote: 3, VotePick: 4, Mayor: 5, InterGo: 6, Gun: 7, Card: 8, Terror: 9, Shake: 10,
  Defuse: 11, Wire: 12,
  Team: 20, Save: 21, Inquire: 22, Shoot: 23, Kane: 24, Revive: 25, DieHard: 26, Silence: 27, Report: 28,
  Guard: 29, Commando: 30, GiveGun: 31, Interrogate: 32, Link: 33, Drunk: 34, LecterSave: 35, Hostage: 36,
  Shayad: 37, Block: 38, Flip: 39, NostraPick: 40, NostraSide: 41, FinalShot: 42,
  Bomb: 43, ZodiacShot: 44, Ocean: 45,
} as const;

export const TeamAct = { Pass: 0, Shot: 1, Sixth: 2, Nato: 3, Buy: 4, Negotiate: 5, Sodagari: 6 } as const;

export const StepKind = {
  Talk: 1, Status: 2, Vote1: 3, Mayor: 4, Vote2: 5, VotePick: 6, InterGo: 7, Gun: 8, Card: 9, Terror: 10,
  Shake: 11, Night: 12, Morning: 13, Over: 14, Noon: 15, Bomb: 16,
} as const;

export const Talk = { Intro: 0, Day: 1, Defense: 2, Will: 3, Interrogation: 4, Chaos: 5, Challenge: 6 } as const;

export const Card = {
  Silence: 1, Cuffs: 2, Reveal: 3, MindGod: 4, FaceOff: 5, Green: 6, Red: 7, MindShab: 8, Insomnia: 9, Dizzy: 10, FinalShot: 11,
} as const;

export const Exit = {
  Vote: 1, Shot: 2, Sixth: 3, Nato: 4, Town: 5, Fireback: 6, Ghost: 7, Gun: 8, Terror: 9, Link: 10, Interrogation: 11, Sacrifice: 12, Mind: 13,
  Bomb: 14, Zodiac: 15, Ocean: 16,
} as const;

export const Ev = {
  Start: 0, DayStart: 1, NightStart: 2, Died: 3, NoDeath: 4, Revived: 5, KaneReveal: 6, Silenced: 7, Interrogation: 8,
  SidesOut: 9, Status: 10, StatusDenied: 11, Talk: 12, SilentSkip: 13, VoteCall: 14, Vote: 15, VoteCount: 16,
  Defendants: 17, NoDefendants: 18, Mayor: 19, Lottery: 20, Exit: 21, ArmorBroke: 22, SideReveal: 23, RoleReveal: 24,
  CardDrawn: 25, CardUsed: 26, Gun: 27, GunHolders: 28, Terror: 29, LinkOut: 30, NatoNight: 31, BuyNight: 32,
  NegotiateNight: 33, SodagariNight: 34, Insomnia: 35, Chaos: 36, Shake: 37, Over: 38, NoExit: 39, Tied: 40,
  Returned: 41, Speech: 42, InterCancel: 43, Stalemate: 44, Bomb: 45, BombResult: 46, Noon: 47,
} as const;

export const Note = {
  Team: 0, Inquiry: 1, Nostra: 2, Kane: 3, Report: 4, Blocked: 5, ShotAt: 6, Converted: 7, Joined: 8, BuyFailed: 9,
  Left: 10, NewRole: 11, Gun: 12, NoResult: 13, SideChosen: 14, Sacrificed: 15, Ocean: 16,
} as const;

export const Say = { Pass: 0, Accuse: 1, Trust: 2, Citizen: 3, VoteFor: 4, Defend: 5, Unsure: 6, Agree: 7, ChallengeAsk: 8, ChallengeGive: 9 } as const;

export type Name = (seat: number) => string;

export const roleFa = (r: number): string => ROLES[r]?.fa ?? "";
export const roleDesc = (r: number): string => ROLES[r]?.desc ?? "";
export const roleKey = (r: number): string => ROLES[r]?.key ?? "";
export const isMafiaRole = (r: number): boolean => !!ROLES[r]?.mafia;
export const roleTeam = (r: number): number => (r >= 80 ? Team.Independent : isMafiaRole(r) ? Team.Mafia : Team.Citizen);
export const scenarioFa = (id: number): string => SCENARIOS[id]?.fa ?? SCENARIOS[1]!.fa;
export const teamFa = (t: number): string => (t === Team.Mafia ? "مافیا" : t === Team.Independent ? "مستقل" : "شهروند");

// ─────────────────────────── جمله‌ها ───────────────────────────

const ACCUSE = [
  ["حسم می‌گه {0} مافیاست.", "یه چیزی درباره‌ی {0} درست نیست.", "من رو {0} شک دارم."],
  ["{0} خیلی ساکته؛ مشکوکه.", "{0} هیچی نمی‌گه، این عجیبه.", "سکوت {0} بهم شک می‌ده."],
  ["رأی‌های {0} عجیب بود.", "{0} جایی رأی داد که نباید.", "رأی {0} رو دیدید؟ مشکوکه."],
  ["{0} از آدم مشکوک دفاع کرد.", "چرا {0} پشت اون دراومد؟", "{0} زیادی هوای بقیه رو داره."],
  ["{0} به آدم بی‌گناه گیر داد.", "{0} داره شهر رو گمراه می‌کنه.", "اتهام‌های {0} بوی پاپوش می‌ده."],
  ["دفاع {0} قانع‌کننده نبود.", "{0} خوب دفاع نکرد.", "{0} زیادی هول بود."],
  ["{0} هم‌سوی مافیای لو‌رفته بود.", "{0} با مافیا هم‌صدا بود.", "{0} طرف مافیا رو گرفت."],
];
const TRUST = ["{0} به نظرم پاکه.", "به {0} اعتماد دارم.", "{0} شهرونده، مطمئنم."];
const VOTE = ["من به {0} رأی می‌دم.", "رأی من {0}ه.", "بیاید {0} رو بفرستیم بیرون."];
const CITIZEN = ["من شهروندم، دنبال مافیام.", "من شهروند ساده‌ام.", "من با شهرم."];
const DEFEND = ["من شهروندم، به من رأی ندید.", "اشتباه می‌کنید، من بی‌گناهم.", "منو بفرستید بیرون، شهر ضرر می‌کنه."];
const UNSURE = ["هنوز چیزی دستم نیومده.", "فعلاً نظری ندارم.", "باید بیشتر ببینم."];
const PASS = ["نکست.", "حرفی ندارم.", "بگذریم."];

/** متنِ یک جمله؛ variant برای تنوع. */
export function speechText(kind: number, target: number, reason: number, name: Name, variant = 0): string {
  const pick = (l: string[]) => l[Math.abs(variant) % l.length]!;
  const who = target >= 0 ? name(target) : "";
  switch (kind) {
    case Say.Accuse: return pick(ACCUSE[Math.min(reason, ACCUSE.length - 1)]!).replace("{0}", who);
    case Say.Trust: return pick(TRUST).replace("{0}", who);
    case Say.VoteFor: return pick(VOTE).replace("{0}", who);
    case Say.Citizen: return pick(CITIZEN);
    case Say.Defend: return target >= 0 ? `${who} بی‌گناهه.` : pick(DEFEND);
    case Say.Unsure: return pick(UNSURE);
    case Say.Agree: return `با ${who} موافقم.`;
    case Say.ChallengeAsk: return "✋ چالش می‌خواهم!";
    case Say.ChallengeGive: return `چالش با ${who}.`;
  }
  return pick(PASS);
}

export const SAY_LABEL: Record<number, string> = {
  [Say.Accuse]: "مشکوکم", [Say.Trust]: "پاکه", [Say.VoteFor]: "رأیم اینه", [Say.Citizen]: "من شهروندم",
  [Say.Defend]: "بی‌گناهم", [Say.Unsure]: "هنوز نظری ندارم", [Say.Pass]: "نکست",
};

// ─────────────────────────── کارت‌ها و کارها ───────────────────────────

export const CARD_FA: Record<number, string> = {
  [Card.Silence]: "سکوت بره‌ها", [Card.Cuffs]: "دستبند", [Card.Reveal]: "افشای هویت", [Card.MindGod]: "ذهن زیبا",
  [Card.FaceOff]: "تغییر چهره", [Card.Green]: "مسیر سبز", [Card.Red]: "فرش قرمز", [Card.MindShab]: "ذهن زیبا",
  [Card.Insomnia]: "بی‌خوابی", [Card.Dizzy]: "سرگیجه", [Card.FinalShot]: "شلیک نهایی",
};

export const CARD_DESC: Record<number, string> = {
  [Card.Silence]: "دو نفر را انتخاب کن تا فردا حرف نزنند (وقتی نیمی رفته‌اند، یک نفر).",
  [Card.Cuffs]: "یک نفر را انتخاب کن تا امشب توانایی نداشته باشد.",
  [Card.Reveal]: "نقشت برای همه اعلام می‌شود.",
  [Card.MindGod]: "نوستراداموس را حدس بزن. درست باشد، به بازی برمی‌گردی.",
  [Card.FaceOff]: "نقشت را با یک نفر زنده عوض کن.",
  [Card.Green]: "یک نفر را انتخاب کن تا فردا کسی به او رأی ندهد.",
  [Card.Red]: "یک نفر را انتخاب کن تا فردا مستقیم به دفاع برود.",
  [Card.MindShab]: "نقش دقیق یک نفر زنده را حدس بزن. درست باشد، برمی‌گردی.",
  [Card.Insomnia]: "امشب کسی کاری نمی‌کند.",
  [Card.Dizzy]: "یک نفر را انتخاب کن: امشب توانایی و فردا رأی ندارد.",
  [Card.FinalShot]: "امشب شلیک با توست، نه با مافیا.",
};

export const TEAM_ACT_FA: Record<number, string> = {
  [TeamAct.Shot]: "شلیک", [TeamAct.Sixth]: "حس ششم", [TeamAct.Nato]: "ناتو", [TeamAct.Buy]: "خرید",
  [TeamAct.Negotiate]: "مذاکره", [TeamAct.Sodagari]: "سوداگری",
};

// ─────────────────────────── گرداننده ───────────────────────────

const ORD = ["معارفه", "اول", "دوم", "سوم", "چهارم", "پنجم", "ششم", "هفتم", "هشتم", "نهم", "دهم"];
export const ordinal = (n: number): string => ORD[n] ?? `${fa(n)}‌م`;
export const dayTitle = (d: number): string => (d === 0 ? "روز معارفه" : `روز ${ordinal(d)}`);
export const nightTitle = (d: number): string => (d === 0 ? "شب معارفه" : `شب ${ordinal(d)}`);

export const WAKE: Record<number, string> = {
  [Ask.Team]: "مافیا بیدار شود…", [Ask.FinalShot]: "صاحب شلیک نهایی بیدار شود…",
  [Ask.NostraPick]: "نوستراداموس بیدار شود و سه نفر را نشان دهد…", [Ask.NostraSide]: "نوستراداموس سمتش را انتخاب کند…",
  [Ask.Save]: "دکتر بیدار شود…", [Ask.Inquire]: "کارآگاه بیدار شود…", [Ask.Shoot]: "تک‌تیرانداز بیدار شود…",
  [Ask.Kane]: "همشهری کین بیدار شود…", [Ask.Revive]: "کنستانتین بیدار شود…", [Ask.DieHard]: "جان‌سخت بیدار شود…",
  [Ask.Silence]: "روانپزشک بیدار شود…", [Ask.Report]: "خبرنگار بیدار شود…", [Ask.Guard]: "نگهبان بیدار شود…",
  [Ask.Commando]: "تکاور بیدار شود. به تو شلیک شد…", [Ask.GiveGun]: "تفنگدار بیدار شود…",
  [Ask.Interrogate]: "بازپرس بیدار شود…", [Ask.Link]: "محقق بیدار شود…", [Ask.Drunk]: "ساقی بیدار شود…",
  [Ask.LecterSave]: "دکتر لکتر بیدار شود…", [Ask.Hostage]: "گروگان‌گیر بیدار شود…", [Ask.Shayad]: "شیاد بیدار شود…",
  [Ask.Block]: "ماتادور بیدار شود…", [Ask.Flip]: "جوکر بیدار شود…",
  [Ask.Bomb]: "بمب‌گذار بیدار شود…", [Ask.ZodiacShot]: "زودیاک بیدار شود…", [Ask.Ocean]: "اوشن بیدار شود…",
  [Ask.Defuse]: "خواب نیمروزی. محافظ بیدار شود…",
};

/** صدای بیدارباش؛ ماتادور و شعبده‌باز یک کار دارند (Block) و با نامِ نقش صدا زده می‌شوند. */
export const wakeText = (ask: number, role = 0): string =>
  ask === Ask.Block && role === MAGICIAN ? "شعبده‌باز بیدار شود…" : WAKE[ask] ?? "";

/** نقش‌های زودیاک (همان MafiaRole). */
export const ZODIAC = 81, MAGICIAN = 53, BODYGUARD = 22;

/** رنگِ سیمِ k (۱ تا ۴) — رمزِ بمب. */
export const WIRE_FA = ["", "قرمز", "آبی", "سبز", "زرد"] as const;
export const wireFa = (k: number): string => WIRE_FA[k] ?? "";
export const NOON_TITLE = "خواب نیمروزی";

/**
 * چتِ میزِ مافیا وقتی شهر خواب است بسته است — برای تماشاچی هم (سرور: MafiaTable.asleep، قاعده‌ی
 * docs/mafia-plan.md). متنی که نوارِ چتِ تماشاچی به‌جای جعبه‌ی متن نشان می‌دهد؛ "" یعنی چت باز است.
 * همتای MafiaOnlineController.QuietNote ِ اپ.
 */
export function quietNote(m: { over: boolean; night: boolean; stepKind: number }): string {
  if (m.over) return "";
  if (m.stepKind === StepKind.Noon) return "خواب نیمروزی است؛ چت تا بیدار شدن شهر بسته است";
  return m.night ? "شب است؛ چت تا صبح بسته است" : "";
}

/** دستورِ کار زیرِ عنوانِ پرسش. */
export function instruction(ask: number, min: number, max: number): string {
  switch (ask) {
    case Ask.Team: return "هدف امشب را لمس کن.";
    case Ask.FinalShot: return "به کسی شلیک کن.";
    case Ask.NostraPick: return "سه نفر را لمس کن تا بفهمی چند تایشان مافیا هستند.";
    case Ask.Save: return max >= 2 ? "یک یا دو نفر را برای نجات لمس کن." : "یک نفر را برای نجات لمس کن.";
    case Ask.Inquire: return "از چه کسی استعلام می‌گیری؟";
    case Ask.Shoot: return "شلیک می‌کنی؟ به شهروند بزنی خودت می‌روی.";
    case Ask.Commando: return "یک تیر داری. به شهروند بزنی خودت می‌روی.";
    case Ask.Kane: return "استعلام یک‌باره‌ات؟ اگر مافیا باشد، تو هم می‌روی.";
    case Ask.Revive: return "چه کسی را برمی‌گردانی؟";
    case Ask.DieHard: return "بخواهی، فردا سمت بیرون‌رفته‌ها اعلام می‌شود.";
    case Ask.Silence: return "چه کسی فردا ساکت باشد؟";
    case Ask.Report: return "درباره‌ی چه کسی می‌پرسی؟";
    case Ask.Guard: return max >= 2 ? "یک یا دو نفر را از گروگان‌گیر محافظت کن." : "یک نفر را از گروگان‌گیر محافظت کن.";
    case Ask.GiveGun: return "نوع تفنگ را انتخاب کن و بعد یک نفر را لمس کن.";
    case Ask.Interrogate: return "دو نفر را برای بازجویی لمس کن.";
    case Ask.Link: return "خودت را به چه کسی گره می‌زنی؟";
    case Ask.Drunk: return "چه کسی را مست می‌کنی؟";
    case Ask.LecterSave: return "کدام هم‌تیمی را نجات می‌دهی؟";
    case Ask.Hostage: return "چه کسی را گروگان می‌گیری؟";
    case Ask.Shayad: return "چه کسی را انتخاب می‌کنی؟ اگر کارآگاه باشد، جواب‌هایش منفی می‌شود.";
    case Ask.Block: return "توان چه کسی را امشب می‌گیری؟";
    case Ask.Flip: return "استعلام چه کسی برعکس شود؟";
    case Ask.Speak: return "نوبت توست. یک نفر را لمس کن یا جمله‌ای بگو.";
    case Ask.Status: return "استعلام وضعیت بخواهیم؟";
    case Ask.Vote: return "دستت را بالا می‌بری؟";
    case Ask.VotePick: return "به یکی از متهم‌ها رأی بده.";
    case Ask.Mayor: return "شهردار: رأی‌گیری را لغو می‌کنی یا یکی را مستقیم بیرون می‌فرستی؟";
    case Ask.InterGo: return "بازجویی ادامه پیدا کند؟";
    case Ask.Gun: return "تفنگ داری. شلیک می‌کنی؟";
    case Ask.Terror: return "یک نفر را با خودت ببر.";
    case Ask.Shake: return "با چه کسی دست می‌دهی؟";
    case Ask.NostraSide: return min >= 1 ? "دو مافیا یا بیشتر دیدی: باید با مافیا باشی." : "با کدام سمت بازی می‌کنی؟";
    case Ask.Card: return "کارت را اجرا کن.";
    case Ask.Bomb: return "رنگ سیم خنثی‌کننده را بزن و جلوی یک نفر بمب بگذار. فقط یک بار.";
    case Ask.ZodiacShot: return "به چه کسی شلیک می‌کنی؟ به محافظ بزنی خودت می‌روی.";
    case Ask.Ocean: return "چه کسی را بیدار می‌کنی؟ شهروند نباشد، خودت می‌روی.";
    case Ask.Defuse: return "خودت را فدا می‌کنی؟ سیمی را ببُر: درست باشد هر دو می‌مانید، غلط باشد خودت می‌روی.";
    case Ask.Wire: return "جلوی تو بمب است! یکی از چهار سیم را ببُر.";
  }
  return "";
}

// ─────────────────────────── اعلام‌ها ───────────────────────────

export type MfEvent = { kind: number; day: number; night: boolean; a: number; b: number; c: number; list: number[] };
export type MfNote = { kind: number; day: number; a: number; b: number; role: number; list: number[] };

const names = (l: number[], name: Name) => l.map(name).join("، ");

/** یک خط برای یک رویدادِ علنی؛ "" یعنی نشان داده نمی‌شود. */
export function eventText(e: MfEvent, name: Name): string {
  switch (e.kind) {
    case Ev.Died: return e.b === Exit.Sixth ? `${name(e.a)} با حس ششم پدرخوانده بیرون رفت.` : `دیشب ${name(e.a)} بیرون رفت.`;
    case Ev.NoDeath: return "دیشب کسی بیرون نرفت.";
    case Ev.Revived: return `${name(e.a)} به بازی برگشت!`;
    case Ev.KaneReveal: return `گرداننده: ${name(e.a)} مافیاست!`;
    case Ev.Silenced: return `${name(e.a)} امروز ساکت است.`;
    case Ev.Interrogation: return `${name(e.a)} و ${name(e.b)} به بازجویی می‌روند.`;
    case Ev.Status: return `استعلام وضعیت: ${fa(e.a)} مافیا و ${fa(e.b)} شهروند بیرون رفته‌اند${e.c > 0 ? `، و ${fa(e.c)} مستقل.` : "."}`;
    case Ev.StatusDenied: return "اکثریت استعلام نخواست.";
    case Ev.Defendants: return `به دفاع می‌روند: ${names(e.list, name)}`;
    case Ev.NoDefendants: return "کسی به نصاب رأی نرسید.";
    case Ev.Mayor: return e.b === 1 ? "شهردار رأی‌گیری را لغو کرد." : `شهردار ${name(e.c)} را مستقیم بیرون فرستاد.`;
    case Ev.Lottery: return `قرعه‌ی مرگ به نام ${name(e.a)} افتاد.`;
    case Ev.Exit:
      switch (e.b) {
        case Exit.Vote: return `${name(e.a)} با رأی شهر بیرون رفت.`;
        case Exit.Gun: return `${name(e.a)} با تیر تفنگ بیرون رفت.`;
        case Exit.Terror: return `${name(e.a)} با تروریست رفت.`;
        case Exit.Link: return `${name(e.a)} با گره‌ی محقق رفت.`;
        case Exit.Interrogation: return `${name(e.a)} در بازجویی بیرون رفت.`;
        case Exit.Mind: return `${name(e.a)} با ذهن زیبا بیرون رفت.`;
        case Exit.Bomb: return `${name(e.a)} با بمب بیرون رفت.`;
      }
      return `${name(e.a)} بیرون رفت.`;
    case Ev.NoExit: return "کسی بیرون نرفت.";
    case Ev.Tied: return "تساوی شد.";
    case Ev.ArmorBroke: return `زره ${name(e.a)} شکست، ولی در بازی ماند.`;
    case Ev.SideReveal: return `${name(e.a)} ${e.b === Team.Mafia ? "مافیا" : "شهروند"} بود.`;
    case Ev.RoleReveal: return `نقش ${name(e.a)}: ${roleFa(e.b)}`;
    case Ev.CardDrawn: return `${name(e.a)} کارت «${CARD_FA[e.b] ?? ""}» را کشید.`;
    case Ev.Gun: return e.c === 1 ? `${name(e.a)} به ${name(e.b)} شلیک کرد!` : `${name(e.a)} به ${name(e.b)} شلیک کرد… تفنگ مشقی بود.`;
    case Ev.GunHolders: return `تفنگ دارند: ${names(e.list, name)}`;
    case Ev.Terror: return `${name(e.a)} تروریست بود و ${name(e.b)} را با خودش برد!`;
    case Ev.LinkOut: return `${name(e.a)} محقق بود؛ ${name(e.b)} هم با او رفت.`;
    case Ev.NatoNight: return "دیشب شب ناتو بود.";
    case Ev.BuyNight: return "دیشب مافیا خرید کرد.";
    case Ev.NegotiateNight: return "دیشب مافیا مذاکره کرد.";
    case Ev.SodagariNight: return "دیشب رئیس سوداگری کرد.";
    case Ev.Insomnia: return "بی‌خوابی: امشب کسی کاری نکرد.";
    case Ev.Chaos: return "کی‌آس! سه نفر مانده‌اند — دست بدهید.";
    case Ev.Returned: return `${name(e.a)} درست حدس زد و به بازی برگشت!`;
    case Ev.SidesOut: {
      const parts: string[] = [];
      for (let i = 0; i + 1 < e.list.length; i += 2) parts.push(`${name(e.list[i]!)}: ${teamFa(e.list[i + 1]!)}`);
      return `جان‌سخت استعلام گرفت — ${parts.join("، ")}`;
    }
    case Ev.InterCancel: return "بازپرس بازجویی را لغو کرد.";
    case Ev.SilentSkip: return `${name(e.a)} ساکت است و نوبتش رد شد.`;
    case Ev.Stalemate: return "بازی به بن‌بست رسید.";
    case Ev.Bomb: return `جلوی ${name(e.a)} بمب گذاشته‌اند! آخر گفت‌وگو باید خنثی شود.`;
    case Ev.Noon: return "خواب نیمروزی: همه چشم‌ها بسته…";
    case Ev.BombResult:
      if (e.c === 2) return `بمب جلوی ${name(e.a)} خنثی شد.`;
      if (e.c === 1) return `${name(e.a)} سیم ${wireFa(e.b)} را برید — خنثی شد!`;
      return `${name(e.a)} سیم ${wireFa(e.b)} را برید… بمب ترکید!`;
    case Ev.Over: return e.a === Team.Mafia ? "مافیا برد." : e.a === Team.Independent ? "زودیاک برد." : "شهروندها بردند.";
  }
  return "";
}

/** یادداشتِ خصوصی (فقط خودم می‌بینم)؛ "" یعنی هیچ. */
export function noteText(n: MfNote, name: Name): string {
  switch (n.kind) {
    case Note.Team: return n.list.length === 0 ? "تو تنها مافیای این میزی." : `هم‌تیمی‌هایت: ${names(n.list, name)}`;
    case Note.Inquiry: return n.b === 1 ? `استعلام ${name(n.a)}: مثبت (مافیا)` : `استعلام ${name(n.a)}: منفی`;
    case Note.Nostra: return `از ${names(n.list, name)}، ${fa(n.b)} نفر مافیا هستند.`;
    case Note.Kane: return n.b === 1 ? `${name(n.a)} مافیاست. فردا همه می‌فهمند.` : `${name(n.a)} مافیا نیست.`;
    case Note.Report: return n.b === 1 ? `${name(n.a)} مذاکره شده است!` : `${name(n.a)} مذاکره نشده.`;
    case Note.Blocked: return "امشب مسدودی؛ کارت اثری ندارد.";
    case Note.ShotAt: return "مافیا امشب به تو شلیک کرد.";
    case Note.Converted: return "تو حالا مافیایی!";
    case Note.Joined: return `${name(n.a)} به تیم ما آمد.`;
    case Note.BuyFailed: return `${name(n.a)} شهروند ساده نبود؛ نشد.`;
    case Note.Left: return `${name(n.a)} دیگر مافیا نیست.`;
    case Note.NewRole: return `نقش تازه‌ات: ${roleFa(n.role)}`;
    case Note.Gun: return "تفنگدار به تو تفنگ داد — جنگی یا مشقی، خودت نمی‌دانی.";
    case Note.SideChosen: return n.b === 1 ? "تو با مافیایی." : "تو با شهری.";
    case Note.Sacrificed: return `${name(n.a)} فدای سوداگری شد.`;
    case Note.Ocean: return n.b === 0 ? `${name(n.a)} شهروند نبود. صبح از بازی می‌روی.` : `گروه اوشن (همه شهروند): ${names(n.list, name)}`;
  }
  return "";
}

/** رویدادی که در دفترچه می‌نشیند (بی نشانه‌های صحنه). */
export const shownInLog = (k: number): boolean =>
  k !== Ev.DayStart && k !== Ev.NightStart && k !== Ev.Talk && k !== Ev.Speech && k !== Ev.Vote
  && k !== Ev.VoteCall && k !== Ev.Start && k !== Ev.VoteCount;
